import { useState, useRef, useMemo } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Upload, Check, AlertTriangle, FileSpreadsheet, ChevronsUpDown, Users } from 'lucide-react';
import { normalizeCategory } from '../lib/categories';

const DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

export const OfficeImportWizard = ({ open, onClose, orderId, onImported }) => {
  const [step, setStep] = useState(1);          // 1=Upload, 2=Review, 3=Confirm
  const [file, setFile] = useState(null);
  const [deliveryDay, setDeliveryDay] = useState('');
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null); // server payload
  const fileInput = useRef(null);

  const reset = () => {
    setStep(1); setFile(null); setDeliveryDay(''); setPreview(null);
  };
  const handleClose = () => { reset(); onClose(); };

  // ---------- Step 1 -> Preview ----------
  const handleUpload = async () => {
    if (!file) { toast.error('Pick a spreadsheet first'); return; }
    if (!deliveryDay) { toast.error('Select the delivery day'); return; }
    const form = new FormData();
    form.append('file', file);
    form.append('delivery_day', deliveryDay);
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/weekly-orders/${orderId}/office-import/preview`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setPreview(res.data);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not parse file');
    } finally {
      setLoading(false);
    }
  };

  // ---------- Step 2 editing helpers ----------
  // Admin can override a dish's recipe link. We mutate the preview dishes array locally.
  const setDishRecipe = (sourceRow, recipe) => {
    setPreview(p => ({
      ...p,
      dishes: p.dishes.map(d => d.source_row !== sourceRow ? d : {
        ...d,
        recipe_match: recipe ? {
          recipe_id: recipe.id,
          recipe_name: recipe.name,
          category: normalizeCategory(recipe.category || ''),
          default_size: 'Standard',
          match_type: 'manual',
        } : null,
        status: recipe ? 'needs_review' : 'unmatched',
      })
    }));
  };

  // Toggle skip on a dish row (admin wants to ignore this row entirely)
  const toggleSkip = (sourceRow) => {
    setPreview(p => ({
      ...p,
      dishes: p.dishes.map(d => d.source_row !== sourceRow ? d : {
        ...d, skipped: !d.skipped,
      })
    }));
  };

  // Toggle action on a client (reuse / create_new is server-decided; we don't need to override).
  // But we DO let admin tweak the key per-column if the file had a typo.
  const setClientKey = (column, newKey) => {
    setPreview(p => ({
      ...p,
      clients: p.clients.map(c => c.column !== column ? c : {
        ...c, source_key: newKey.toUpperCase().slice(0, 3),
      })
    }));
  };

  // ---------- Step 3 -> Confirm ----------
  const handleConfirm = async () => {
    if (!preview) return;
    const payload = {
      delivery_day: preview.delivery_day,
      clients: preview.clients.map(c => ({
        column: c.column,
        source_name: c.source_name,
        source_key: c.source_key,
        action: c.action,                       // reuse | create_new
        existing_client_id: c.match?.existing_client_id || '',
      })),
      dishes: preview.dishes
        .filter(d => !d.skipped && d.any_qty)
        .map(d => ({
          source_row: d.source_row,
          dish_name: d.dish_name,
          recipe_id: d.recipe_match?.recipe_id || '',
          recipe_name: d.recipe_match?.recipe_name || '',
          category: d.recipe_match?.category || '',
          default_size: d.recipe_match?.default_size || 'Standard',
          match_type: d.recipe_match?.match_type || '',
          client_qty: Object.fromEntries(
            Object.entries(d.client_qty).map(([col, qty]) => [String(col), qty])
          ),
        })),
    };
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/weekly-orders/${orderId}/office-import/confirm`,
        payload
      );
      toast.success(`${res.data.created_lines} office lines imported${res.data.replaced_lines ? ` (replaced ${res.data.replaced_lines} previous)` : ''}`);
      onImported?.();
      handleClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save office import');
    } finally {
      setLoading(false);
    }
  };

  // ---------- Derived ----------
  const toCreateLines = useMemo(() => {
    if (!preview) return 0;
    return preview.dishes
      .filter(d => !d.skipped && d.any_qty)
      .reduce((n, d) => n + Object.values(d.client_qty).filter(q => q > 0).length, 0);
  }, [preview]);

  const unmatchedWithQty = useMemo(() => {
    if (!preview) return [];
    return preview.dishes.filter(d => d.any_qty && !d.skipped && !d.recipe_match);
  }, [preview]);

  const matchedWithQty = useMemo(() => {
    if (!preview) return [];
    return preview.dishes.filter(d => d.any_qty && !d.skipped && d.recipe_match);
  }, [preview]);

  // ---------- Render ----------
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="office-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-[var(--color-primary)]" />
            Office Import — Step {step} of 3
          </DialogTitle>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-2 text-xs mb-2">
          {['Upload', 'Review', 'Confirm'].map((label, i) => (
            <div key={label} className="flex items-center gap-2">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center font-semibold
                ${step > i+1 ? 'bg-green-500 text-white' :
                  step === i+1 ? 'bg-[var(--color-primary)] text-white' :
                  'bg-gray-200 text-gray-500'}`}>
                {step > i+1 ? <Check className="w-3 h-3" /> : i+1}
              </div>
              <span className={step === i+1 ? 'font-semibold' : 'text-gray-500'}>{label}</span>
              {i < 2 && <div className="w-8 h-px bg-gray-300" />}
            </div>
          ))}
        </div>

        {/* ------------------------- STEP 1 ------------------------- */}
        {step === 1 && (
          <div className="space-y-4" data-testid="office-import-step1">
            <p className="text-sm text-gray-600">
              Upload the office orders spreadsheet. The system will match dishes against your Final Recipes;
              you&apos;ll confirm any unmatched links before saving.
            </p>
            <div>
              <Label className="text-sm">Delivery Day (applies to every line in this import)</Label>
              <Select value={deliveryDay} onValueChange={setDeliveryDay}>
                <SelectTrigger className="mt-1" data-testid="office-delivery-day-select">
                  <SelectValue placeholder="Pick a delivery day…" />
                </SelectTrigger>
                <SelectContent>
                  {DAYS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Production day is assigned later per line on the orders table.
              </p>
            </div>
            <div>
              <Label className="text-sm">Spreadsheet (.xlsx)</Label>
              <div
                onClick={() => fileInput.current?.click()}
                className="mt-1 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-[var(--color-primary)] transition-colors"
                data-testid="office-file-dropzone"
              >
                <Upload className="w-6 h-6 mx-auto text-gray-400 mb-1" />
                <div className="text-sm font-medium">{file ? file.name : 'Click to select .xlsx file'}</div>
                {file && <div className="text-xs text-gray-500 mt-0.5">{(file.size / 1024).toFixed(1)} KB</div>}
              </div>
              <input
                ref={fileInput}
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="office-file-input"
              />
            </div>
          </div>
        )}

        {/* ------------------------- STEP 2 ------------------------- */}
        {step === 2 && preview && (
          <div className="space-y-4" data-testid="office-import-step2">
            {/* Reconciliation banner */}
            <div className="grid grid-cols-4 gap-2 p-3 bg-gray-50 rounded-lg text-sm">
              <Stat label="Clients" value={preview.reconciliation.client_count} />
              <Stat label="Dish rows (file)" value={preview.reconciliation.dish_row_count} />
              <Stat label="Linked (auto)" value={matchedWithQty.length} color="text-green-600" />
              <Stat label="Unlinked (will import)" value={unmatchedWithQty.length}
                    color={unmatchedWithQty.length ? 'text-amber-600' : 'text-gray-500'} />
            </div>

            {preview.existing_office_lines_count > 0 && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                <div className="text-sm text-red-700">
                  <strong>{preview.existing_office_lines_count}</strong> office line{preview.existing_office_lines_count === 1 ? '' : 's'} already exist on this week. They will be <strong>replaced</strong> when you confirm.
                </div>
              </div>
            )}

            {/* Clients section */}
            <section>
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-1">
                <Users className="w-4 h-4" /> Office Clients ({preview.clients.length})
              </h4>
              <div className="border rounded-lg overflow-hidden">
                <table className="data-table text-sm">
                  <thead><tr>
                    <th>Name (from file)</th><th>Key</th><th>Action</th><th>Already in this week?</th>
                  </tr></thead>
                  <tbody>
                    {preview.clients.map(c => (
                      <tr key={c.column}>
                        <td className="font-medium">{c.source_name}</td>
                        <td>
                          <input
                            value={c.source_key}
                            onChange={(e) => setClientKey(c.column, e.target.value)}
                            className="w-16 px-2 py-1 text-xs border rounded uppercase font-semibold"
                            data-testid={`office-client-key-${c.column}`}
                          />
                        </td>
                        <td>
                          {c.action === 'reuse' ? (
                            <span className="badge-pill badge-teal text-[10px]">Reuse existing</span>
                          ) : (
                            <span className="badge-pill badge-gray text-[10px]">New — will be created</span>
                          )}
                        </td>
                        <td className="text-xs text-gray-500">
                          {c.already_in_snapshot ? 'Yes — key will update' : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Unmatched dishes */}
            {unmatchedWithQty.length > 0 && (
              <section>
                <h4 className="text-sm font-semibold text-amber-700 mb-2 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" />
                  Unlinked Dishes ({unmatchedWithQty.length}) — link now or on the Unassigned tab after import
                </h4>
                <p className="text-xs text-gray-500 mb-2">
                  These will still be imported. Any left unlinked here can be linked later from the Unassigned tab — the recipe mapping is saved either way for future imports.
                </p>
                <div className="border rounded-lg overflow-hidden">
                  <table className="data-table text-sm">
                    <thead><tr>
                      <th>Row</th><th>Dish Name (file)</th><th>Pick Final Recipe</th><th>Total Qty</th><th></th>
                    </tr></thead>
                    <tbody>
                      {unmatchedWithQty.map(d => (
                        <tr key={d.source_row}>
                          <td className="text-xs text-gray-500">R{d.source_row}</td>
                          <td className="font-medium">{d.dish_name}</td>
                          <td>
                            <RecipePicker
                              recipes={preview.all_recipes}
                              onPick={(r) => setDishRecipe(d.source_row, r)}
                              testid={`office-recipe-picker-${d.source_row}`}
                            />
                          </td>
                          <td className="text-right">{Object.values(d.client_qty).reduce((a,b)=>a+b,0)}</td>
                          <td>
                            <button
                              onClick={() => toggleSkip(d.source_row)}
                              className="text-xs text-gray-500 underline hover:text-gray-700"
                              data-testid={`office-skip-${d.source_row}`}
                            >Skip</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {/* Matched dishes (collapsed by default would be nicer; keeping simple) */}
            {matchedWithQty.length > 0 && (
              <section>
                <h4 className="text-sm font-semibold text-green-700 mb-2 flex items-center gap-1">
                  <Check className="w-4 h-4" /> Matched Dishes ({matchedWithQty.length})
                </h4>
                <div className="border rounded-lg overflow-hidden max-h-56 overflow-y-auto">
                  <table className="data-table text-sm">
                    <thead><tr>
                      <th>Dish</th><th>Linked Recipe</th><th>Category</th><th>Size</th><th>Total Qty</th>
                    </tr></thead>
                    <tbody>
                      {matchedWithQty.map(d => (
                        <tr key={d.source_row}>
                          <td className="font-medium">{d.dish_name}</td>
                          <td>
                            <RecipePicker
                              recipes={preview.all_recipes}
                              currentValue={d.recipe_match.recipe_id}
                              currentLabel={d.recipe_match.recipe_name}
                              onPick={(r) => setDishRecipe(d.source_row, r)}
                              testid={`office-recipe-picker-matched-${d.source_row}`}
                              compact
                            />
                          </td>
                          <td><span className="badge-pill badge-gray text-[10px]">{d.recipe_match.category}</span></td>
                          <td className="text-xs">{d.recipe_match.default_size}</td>
                          <td className="text-right">{Object.values(d.client_qty).reduce((a,b)=>a+b,0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </div>
        )}

        {/* ------------------------- STEP 3 ------------------------- */}
        {step === 3 && preview && (
          <div className="space-y-3" data-testid="office-import-step3">
            <p className="text-sm">Review the final numbers before saving:</p>
            <ul className="text-sm space-y-1 bg-gray-50 p-3 rounded-lg">
              <li>Delivery day: <strong>{preview.delivery_day}</strong></li>
              <li>Clients to add/update in this week&apos;s snapshot: <strong>{preview.clients.length}</strong></li>
              <li>Dish rows being imported: <strong>{matchedWithQty.length + unmatchedWithQty.length}</strong>
                {unmatchedWithQty.length > 0 && (
                  <span className="text-amber-700"> ({unmatchedWithQty.length} unlinked — link later on Unassigned tab)</span>
                )}
              </li>
              <li>Order lines to create: <strong>{toCreateLines}</strong></li>
              {preview.existing_office_lines_count > 0 && (
                <li className="text-red-600">
                  <strong>{preview.existing_office_lines_count}</strong> existing office line{preview.existing_office_lines_count === 1 ? '' : 's'} will be replaced.
                </li>
              )}
            </ul>
          </div>
        )}

        {/* ------------------------- Footer ------------------------- */}
        <DialogFooter className="gap-2">
          {step === 1 && (
            <>
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button onClick={handleUpload} disabled={loading || !file || !deliveryDay}
                      className="btn-primary" data-testid="office-upload-next-btn">
                {loading ? 'Parsing…' : 'Next: Review'}
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button onClick={() => setStep(3)} className="btn-primary"
                      disabled={toCreateLines === 0}
                      data-testid="office-review-next-btn">
                Next: Confirm ({toCreateLines} lines)
              </Button>
            </>
          )}
          {step === 3 && (
            <>
              <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
              <Button onClick={handleConfirm} disabled={loading}
                      className="btn-primary" data-testid="office-confirm-btn">
                {loading ? 'Saving…' : `Import ${toCreateLines} lines`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// -------- Recipe picker (combobox) --------
const RecipePicker = ({ recipes, currentValue, currentLabel, onPick, testid, compact }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          className={`justify-between ${compact ? 'h-7 text-xs min-w-[180px]' : 'h-8 text-xs min-w-[220px]'}`}
          data-testid={testid}
        >
          <span className="truncate">{currentLabel || 'Pick a recipe…'}</span>
          <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0">
        <Command>
          <CommandInput placeholder="Search recipes…" className="h-8" />
          <CommandList>
            <CommandEmpty>No recipes found.</CommandEmpty>
            <CommandGroup>
              {recipes.map(r => (
                <CommandItem
                  key={r.id}
                  value={r.name}
                  onSelect={() => { onPick(r); setOpen(false); }}
                >
                  <Check className={`mr-2 h-3 w-3 ${currentValue === r.id ? 'opacity-100' : 'opacity-0'}`} />
                  <span className="text-xs">{r.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const Stat = ({ label, value, color = 'text-gray-800' }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
    <div className={`text-lg font-semibold ${color}`}>{value}</div>
  </div>
);
